/**
 * CalendarIntegrationModal
 *
 * Modal para integrar calendários externos (Google Calendar, Outlook, etc.)
 * Usa arquitetura OAuth já implementada no módulo provider
 */

import { useState } from 'react';
import { api } from '@/lib/api';
import { useUserCalendarIntegrations } from '../hooks/useUserCalendarIntegrations';
import { Modal, ModalHeader, ModalContent, ModalFooter, Button } from '@/shared/components/ui';
import { Icon } from '@/shared/icons'
interface CalendarIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CalendarIntegrationModal: React.FC<CalendarIntegrationModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Buscar integrações ativas do usuário
  const { data: integrations = [], isLoading: isLoadingIntegrations } = useUserCalendarIntegrations();

  // Helper para verificar se um provider já está conectado
  const isProviderConnected = (providerId: string): boolean => {
    return integrations.some(
      (integration) => integration.providerId === providerId && integration.status === 'active'
    );
  };

  // Helper para verificar se um provider tem integração falhada
  const getFailedIntegration = (providerId: string) => {
    return integrations.find(
      (integration) =>
        integration.providerId === providerId &&
        (integration.status === 'auth_failed' || integration.status === 'disconnected')
    );
  };

  const handleIntegrateGoogle = async () => {
    setLoading(true);
    setError(null);

    try {
      // Verificar se há integração falhada existente
      const failedIntegration = getFailedIntegration('google-calendar');

      let userIntegrationId: string;

      if (failedIntegration) {
        // Se há integração falhada, reconectar usando o ID existente
        console.log('🔄 Reconectando integração falhada:', failedIntegration.id);
        userIntegrationId = failedIntegration.id;
      } else {
        // Se não há integração, criar nova
        console.log('📝 Creating user-integration for Google Calendar...');
        const createResponse = await api.post('/user-integrations', {
          providerId: 'google-calendar',
          // appIntegrationId será selecionado automaticamente no backend
          // baseado em capability 'calendar_sync'
        });

        userIntegrationId = createResponse.data.data.id;
        console.log(`✅ User-integration created: ${userIntegrationId}`);
      }

      // Iniciar OAuth usando endpoint existente do provider
      console.log('🔐 Initiating OAuth flow...');
      const oauthResponse = await api.post(
        `/provider/oauth/initiate/${userIntegrationId}`
      );

      const oauthUrl = oauthResponse.data.data.oauthUrl;
      console.log(`✅ OAuth URL received: ${oauthUrl}`);

      // Redirecionar para OAuth do Google
      console.log('🔄 Redirecting to Google OAuth...');
      window.location.href = oauthUrl;
    } catch (err: any) {
      console.error('❌ Error integrating Google Calendar:', err);
      setError(
        err.response?.data?.message ||
        'Erro ao iniciar integração. Tente novamente.'
      );
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="md">
      {/* Header — Modal shared (F72), título 18 (F74) */}
      <ModalHeader title="Integrar calendários" onClose={onClose} />

      <ModalContent className="space-y-4">
          {/* Error Message */}
          {error && (
            <div
              className="p-3 rounded-lg border"
              style={{
                backgroundColor: '#fef2f2',
                borderColor: '#fecaca',
                color: '#991b1b'
              }}
            >
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Info Box */}
          <div
            className="p-3 rounded-lg border"
            style={{
              backgroundColor: '#eff6ff',
              borderColor: '#bfdbfe'
            }}
          >
            <div className="flex items-start gap-2">
              <Icon name="check" className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">
                  O que acontece ao integrar?
                </p>
                <ul className="text-xs text-blue-700 mt-1 space-y-1">
                  <li>• Seus eventos serão sincronizados automaticamente</li>
                  <li>• Você poderá criar eventos em ambos os calendários</li>
                  <li>• As alterações serão refletidas em tempo real</li>
                  <li>• Suas credenciais são armazenadas com segurança</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Google Calendar Integration Button */}
          <button
            onClick={handleIntegrateGoogle}
            data-testid="calendar-integration-google"
            disabled={loading || isLoadingIntegrations || isProviderConnected('google-calendar')}
            className="w-full flex items-center gap-3 p-4 border-2 rounded-lg transition-all"
            style={{
              borderColor: isProviderConnected('google-calendar')
                ? '#10b981'
                : getFailedIntegration('google-calendar')
                ? '#f59e0b'
                : loading || isLoadingIntegrations
                ? '#cbd5e1'
                : '#e5e7eb',
              backgroundColor: isProviderConnected('google-calendar')
                ? '#ecfdf5'
                : getFailedIntegration('google-calendar')
                ? '#fef3c7'
                : loading || isLoadingIntegrations
                ? '#f9fafb'
                : '#ffffff',
              cursor: loading || isLoadingIntegrations || isProviderConnected('google-calendar') ? 'not-allowed' : 'pointer',
              opacity: isLoadingIntegrations ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!loading && !isLoadingIntegrations && !isProviderConnected('google-calendar')) {
                if (getFailedIntegration('google-calendar')) {
                  e.currentTarget.style.borderColor = '#f59e0b';
                  e.currentTarget.style.backgroundColor = '#fde68a';
                } else {
                  e.currentTarget.style.borderColor = '#3b82f6';
                }
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && !isLoadingIntegrations && !isProviderConnected('google-calendar')) {
                if (getFailedIntegration('google-calendar')) {
                  e.currentTarget.style.borderColor = '#f59e0b';
                  e.currentTarget.style.backgroundColor = '#fef3c7';
                } else {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }
              }
            }}
          >
            <div
              className="w-[38px] h-[38px] rounded-[10px] flex items-center justify-center flex-shrink-0 font-extrabold text-white"
              style={{ backgroundColor: '#ea4335' }}
            >
              G
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium" style={{ color: '#1e293b' }}>
                Google Calendar
              </p>
              <p className="text-xs" style={{ color: '#64748b' }}>
                {isLoadingIntegrations
                  ? 'Verificando status...'
                  : isProviderConnected('google-calendar')
                  ? 'Calendário conectado e sincronizando'
                  : getFailedIntegration('google-calendar')
                  ? 'Autenticação falhou - Clique para reconectar'
                  : loading
                  ? 'Iniciando integração...'
                  : 'Sincronize seus eventos do Google'}
              </p>
            </div>
            {(loading || isLoadingIntegrations) && (
              <div
                className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: '#3b82f6' }}
              />
            )}
            {!isLoadingIntegrations && isProviderConnected('google-calendar') && (
              <div
                className="flex items-center gap-1 px-2 py-1 rounded-full"
                style={{
                  backgroundColor: '#10b981',
                  color: '#ffffff'
                }}
              >
                <Icon name="check-circle" className="w-3 h-3" />
                <span className="text-xs font-medium">Conectado</span>
              </div>
            )}
            {!isLoadingIntegrations && getFailedIntegration('google-calendar') && (
              <div
                className="flex items-center gap-1 px-2 py-1 rounded-full"
                style={{
                  backgroundColor: '#f59e0b',
                  color: '#ffffff'
                }}
              >
                <Icon name="alert-triangle" className="w-3 h-3" />
                <span className="text-xs font-medium">Desconectado</span>
              </div>
            )}
          </button>

          {/* Outlook Calendar (Coming Soon) */}
          <button
            disabled
            className="w-full flex items-center gap-3 p-4 border-2 rounded-lg opacity-50 cursor-not-allowed"
            style={{ borderColor: '#e5e7eb' }}
          >
            <div
              className="w-[38px] h-[38px] rounded-[10px] flex items-center justify-center flex-shrink-0 font-extrabold text-white"
              style={{ backgroundColor: '#0078d4' }}
            >
              O
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium" style={{ color: '#1e293b' }}>
                Outlook Calendar
              </p>
              <p className="text-xs" style={{ color: '#64748b' }}>
                Em breve
              </p>
            </div>
          </button>

          {/* iCloud Calendar (Coming Soon) */}
          <button
            disabled
            className="w-full flex items-center gap-3 p-4 border-2 rounded-lg opacity-50 cursor-not-allowed"
            style={{ borderColor: '#e5e7eb' }}
          >
            <div
              className="w-[38px] h-[38px] rounded-[10px] flex items-center justify-center flex-shrink-0 font-extrabold text-white"
              style={{ backgroundColor: '#111827' }}
            >
              i
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium" style={{ color: '#1e293b' }}>
                iCloud Calendar
              </p>
              <p className="text-xs" style={{ color: '#64748b' }}>
                Em breve
              </p>
            </div>
          </button>
      </ModalContent>

      {/* Footer — Cancelar secondary (F71) */}
      <ModalFooter>
        <Button
          variant="secondary"
          onClick={onClose}
          data-testid="calendar-integration-cancel"
        >
          Cancelar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

CalendarIntegrationModal.displayName = 'CalendarIntegrationModal';
